import { useEffect, useState } from 'react';
import { CrudForm } from '../shared/CrudPage';
import { enumEntries, purchaseOrderService, productService } from '@kss/api';
import type {
  PurchaseOrder,
  PurchaseOrderCreate,
  PurchaseOrderFilter,
  PurchaseOrderItem,
} from '@kss/api';
import {
  loadStoreOptions,
  loadSupplierOptions,
  loadWarehouseOptions,
  loadProductOptions,
} from '../shared/formLoaders';
import { SearchableSelect, Button, type SearchableSelectOption } from '@kss/ui';
import { Plus, Trash2 } from 'lucide-react';
import NumberField from '../../components/NumberField';

/* `warehouseId` added 2026-05-21 — backend rejects POs without it
   (`REQUIRED_FIELD:WarehouseId` from POST /api/PurchaseOrder). The
   field was previously omitted from the form, so the server-side
   validator blanket-failed every submission.

   SSO Issue #7 (2026-07-13) — items[] grid added so POs actually
   carry line-item data. The backend now cascades PurchaseOrderItem
   rows on save (see PurchaseOrderService.MapHeaderWithItems) AND
   increments Stock.Quantity per line (see PurchaseOrderService
   .AddAsync). Prior to this the form was header-only and every PO
   landed with zero PurchaseOrderItem rows and no stock movement. */

type LineRow = {
  key: string; // stable react key so removing a row doesn't reshuffle inputs
  productId: string;
  quantity: number;
  unitCost: number;
  taxRate: number;
};

type F = {
  orderNumber: string;
  orderDate: string;
  expectedDate: string;
  supplierId: string;
  storeId: string;
  warehouseId: string;
  totalAmount: number;
  status: string;
  notes: string;
  isActive: boolean;
  items: LineRow[];
};

/* SSO ISSUE 2b (2026-07-16) — PurchaseOrder status transitions mirror
   SalesOrder for stock semantics: Draft (1) → Confirmed (2) → Shipped
   (3) → Delivered (4). "Delivered" (4) is the trigger that increments
   Stock.Quantity + emits a StockMovement.In audit row (PurchaseOrder
   Service.UpdateAsync). Cancelled (5) is terminal. `StringOrIntJsonConverter`
   on the backend can't unambiguously map every label to the right int
   (its default table has "received"→3 and no "delivered" entry), so
   we send the raw int rather than a label string. */
const PO_STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('PurchaseOrderStatus').map((e) => [e.label, e.value]),
);
const PO_LABEL_BY_STATUS: Record<number, string> = Object.fromEntries(
  enumEntries('PurchaseOrderStatus').map((e) => [e.value, e.label]),
);

const today = new Date().toISOString().slice(0, 10);
const newLineRow = (): LineRow => ({
  key: `l_${Math.random().toString(36).slice(2, 10)}`,
  productId: '',
  quantity: 1,
  unitCost: 0,
  taxRate: 0,
});
const empty: F = {
  orderNumber: '',
  orderDate: today,
  expectedDate: today,
  supplierId: '',
  storeId: '',
  warehouseId: '',
  totalAmount: 0,
  status: 'Draft',
  notes: '',
  isActive: true,
  items: [newLineRow()],
};

/** Line-total: (qty × unitCost) + tax. Tax is derived from taxRate when > 0. */
function computeLineTotal(l: LineRow): number {
  const gross = (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
  const tax = (Number(l.taxRate) || 0) > 0 ? (gross * Number(l.taxRate)) / 100 : 0;
  return Math.round((gross + tax) * 100) / 100;
}
function computeGrandTotal(items: LineRow[]): number {
  return Math.round(items.reduce((acc, l) => acc + computeLineTotal(l), 0) * 100) / 100;
}

/**
 * Editable line-item grid. Persisted product/qty/unitCost rows drive
 * the PurchaseOrderItem table on save; the running total displayed in
 * the header's Total field is derived from these lines client-side so
 * the user can see the number they're about to send.
 */
function ItemsGrid({
  value,
  onChange,
}: {
  value: LineRow[];
  onChange: (next: LineRow[]) => void;
}) {
  /* Product options loaded once for every picker in this grid.
     `productOptionsLoading` gates the trigger label so that on edit
     hydration — where each row already has a hydrated `productId` but
     the option list hasn't come back yet — the SearchableSelect shows
     "Loading…" instead of falling back to the "Pick a product"
     placeholder. Without this the row briefly rendered a blank name
     even after the record loaded (SSO Purchase-Order Edit regression). */
  const [productOptions, setProductOptions] = useState<SearchableSelectOption[]>([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setProductOptionsLoading(true);
    loadProductOptions()
      .then((opts) => {
        if (!cancelled) setProductOptions(opts);
      })
      .catch(() => {
        /* swallow — the picker will still render an empty list and the
           inline error surface handles messaging elsewhere. */
      })
      .finally(() => {
        if (!cancelled) setProductOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When a product is picked and unitCost is still 0, pre-fill from the
  // product's `costPrice` (or fall back to `price`) so the user gets a
  // sensible default. Keeps typing to a minimum for the common case.
  const [productPriceCache, setProductPriceCache] = useState<Record<string, number>>({});
  useEffect(() => {
    if (productOptions.length === 0) return;
    let cancelled = false;
    productService
      .all()
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const r of rows ?? []) {
          const pid = (r as { productId?: string }).productId;
          if (!pid) continue;
          const cost = (r as { costPrice?: number }).costPrice;
          const price = (r as { price?: number }).price;
          map[pid] = Number(cost ?? price ?? 0);
        }
        setProductPriceCache(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [productOptions.length]);

  const patchRow = (idx: number, patch: Partial<LineRow>) => {
    const next = value.slice();
    next[idx] = { ...next[idx], ...patch };
    // Auto-fill unitCost when a product is picked and cost is still zero.
    if (patch.productId && (next[idx].unitCost === 0 || Number.isNaN(next[idx].unitCost))) {
      const cachedCost = productPriceCache[patch.productId];
      if (cachedCost && cachedCost > 0) next[idx].unitCost = cachedCost;
    }
    onChange(next);
  };
  const addRow = () => onChange([...value, newLineRow()]);
  /* Deleting the last row used to immediately re-seed a fresh blank one, so
     the row appeared to "come back" every time it was removed. Allow the grid
     to go genuinely empty — `validate` already blocks saving a PO with no
     usable lines, and "Add row" is right there. */
  const removeRow = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-content-secondary">
          Line items <span className="text-danger">*</span>
        </span>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="h-3.5 w-3.5" /> Add row
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-card-hover text-xs uppercase text-content-muted">
            <tr>
              <th className="w-[38%] px-2 py-2 text-left">Product</th>
              <th className="w-[12%] px-2 py-2 text-right">Qty</th>
              <th className="w-[16%] px-2 py-2 text-right">Unit cost (₹)</th>
              <th className="w-[12%] px-2 py-2 text-right">Tax %</th>
              <th className="w-[16%] px-2 py-2 text-right">Line total (₹)</th>
              <th className="w-[6%] px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {value.map((row, idx) => (
              <tr key={row.key} className="border-t border-border align-top">
                <td className="px-2 py-1.5">
                  <SearchableSelect
                    value={row.productId}
                    onChange={(v) => patchRow(idx, { productId: v })}
                    options={productOptions}
                    /* `loading` tells the trigger to show "Loading…" for
                       an edit-hydrated `productId` while the options
                       array is still empty — otherwise the picker would
                       flash the "Pick a product" placeholder for a
                       row that already has a persisted product. */
                    loading={productOptionsLoading}
                    /* `auto` flips the panel upward when the row sits
                       near the bottom of the viewport, so opening the
                       dropdown no longer buries the fields below the
                       items table. */
                    placement="auto"
                    placeholder="Pick a product"
                    searchPlaceholder="Search products…"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="1"
                    value={row.quantity}
                    onValueChange={(n) => patchRow(idx, { quantity: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="0.01"
                    value={row.unitCost}
                    onValueChange={(n) => patchRow(idx, { unitCost: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="0.01"
                    value={row.taxRate}
                    onValueChange={(n) => patchRow(idx, { taxRate: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5 text-right text-sm text-content-primary">
                  {computeLineTotal(row).toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                    onClick={() => removeRow(idx)}
                    title="Remove row"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {value.length === 0 && (
              <tr className="border-t border-border">
                <td colSpan={6} className="px-2 py-6 text-center text-sm text-content-muted">
                  No line items yet — use <strong>Add row</strong> to add one.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-surface-card-hover">
              <td className="px-2 py-2 text-right text-xs font-medium text-content-secondary" colSpan={4}>
                Grand total
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-content-primary">
                ₹ {computeGrandTotal(value).toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-content-muted">
        The Total (₹) field below is auto-derived from these rows on save. Backend re-runs the same
        math server-side (PurchaseOrderController.RecomputeTotals).
      </p>
    </div>
  );
}

export default function NewPurchaseOrder() {
  return (
    <CrudForm<PurchaseOrder, PurchaseOrderCreate, PurchaseOrderFilter, F>
      title="purchase order"
      service={purchaseOrderService}
      idKey="purchaseOrderId"
      basePath="/panel/purchase-orders"
      emptyForm={empty}
      fields={[
        { key: 'orderNumber', label: 'PO number', required: true, placeholder: 'PO-1001' },
        { key: 'orderDate', label: 'Order date', type: 'date' },
        { key: 'expectedDate', label: 'Expected date', type: 'date' },
        /* Supplier lookup MUST load from `/api/Supplier/all` — the
           previous `loadUserOptions` stored a userId in `supplierId`,
           which the supplier name-map couldn't resolve, so the All-POs
           grid rendered a truncated GUID instead of the supplier
           name. Bug ref: AI Agent 2026-05-21 — "supplyer ID show in
           grid it should be name and ID hide". */
        { key: 'supplierId', label: 'Supplier', type: 'search', loadOptions: loadSupplierOptions, placeholder: 'Pick a supplier' },
        { key: 'storeId', label: 'Store', type: 'search', loadOptions: loadStoreOptions, placeholder: 'Select a store' },
        { key: 'warehouseId', label: 'Warehouse', type: 'search', required: true, loadOptions: loadWarehouseOptions, placeholder: 'Pick a warehouse' },
        {
          key: 'items',
          label: 'Line items',
          type: 'custom',
          fullWidth: true,
          render: (val, set) => (
            <ItemsGrid value={val as LineRow[]} onChange={(next) => set(next as F[keyof F & string])} />
          ),
        },
        {
          key: 'totalAmount',
          label: 'Total (₹) — auto from line items',
          type: 'number',
          required: true,
        },
        { key: 'status', label: 'Status', type: 'select', options: ['Draft', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'].map((v) => ({ value: v, label: v })) },
        { key: 'isActive', label: 'Active', type: 'status' },
        { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        orderNumber: r.orderNumber ?? '',
        orderDate: r.orderDate?.slice(0, 10) ?? today,
        expectedDate: r.expectedDate?.slice(0, 10) ?? today,
        supplierId: r.supplierId ?? '',
        storeId: r.storeId ?? '',
        warehouseId: r.warehouseId ?? '',
        totalAmount: Number(r.totalAmount ?? 0),
        // Backend ships `status` as int (see PurchaseOrder entity /
        // OrderStatus enum); coerce to a label for the dropdown. Fallback
        // when the row already carries a legacy string is a passthrough.
        status:
          typeof r.status === 'number'
            ? PO_LABEL_BY_STATUS[r.status] ?? 'Draft'
            : String(r.status ?? 'Draft'),
        notes: r.notes ?? '',
        isActive: !!r.isActive,
        items:
          (r.items ?? []).length > 0
            ? (r.items ?? []).map(
                (i, idx): LineRow => ({
                  key: `edit_${i.purchaseOrderItemId ?? idx}`,
                  productId: i.productId ?? '',
                  quantity: Number(i.quantity ?? 0),
                  unitCost: Number(i.unitCost ?? 0),
                  taxRate: Number(i.taxRate ?? 0),
                }),
              )
            : [newLineRow()],
      })}
      toEntity={(f, id) => {
        /* SSO ISSUE 2a (2026-07-16) — line items MUST NOT ship a
           `purchaseOrderId` field at all. Backend's PurchaseOrderItem.
           PurchaseOrderId is `Guid` (non-nullable value type); when the
           strict binder sees `""` (empty string on Create) it fails with
           `The JSON value could not be converted to System.Guid. Path:
           $.items[0].items[0].purchaseOrderId` and rejects the whole POST
           with 400. On Update the header id `id` would work, but on Add
           there is no id yet — `MapHeaderWithItems` (in the service)
           always stamps the header id onto each line, so omitting the
           field entirely is the safe path for both flows. Same story for
           `purchaseOrderItemId` — omit and the service seeds a Guid.
           NewPO.wireItems: send { productId, quantity, unitCost, taxRate?,
           total } only. */
        const wireItems: PurchaseOrderItem[] = f.items
          .filter((l) => l.productId && Number(l.quantity) > 0)
          .map((l) => ({
            productId: l.productId,
            quantity: Number(l.quantity),
            unitCost: Number(l.unitCost),
            taxRate: Number(l.taxRate) > 0 ? Number(l.taxRate) : undefined,
            total: computeLineTotal(l),
          }));
        const derivedTotal = computeGrandTotal(f.items);
        // If the user typed a manual total override, keep it; otherwise
        // trust the derived total from lines.
        const total =
          Number(f.totalAmount) > 0 && Math.abs(Number(f.totalAmount) - derivedTotal) > 0.01
            ? Number(f.totalAmount)
            : derivedTotal;
        return {
          purchaseOrderId: id,
          orderNumber: f.orderNumber,
          orderDate: f.orderDate || undefined,
          expectedDate: f.expectedDate || undefined,
          supplierId: f.supplierId || undefined,
          storeId: f.storeId || undefined,
          warehouseId: f.warehouseId || undefined,
          /* Send `subTotal` / `taxAmount` alongside `totalAmount` so the
             backend has the full breakdown when it computes header
             totals from header fields. When items[] is non-empty the
             backend recomputes from lines anyway (see
             PurchaseOrderController.RecomputeTotals). Bug ref: AI Agent
             2026-05-26 "new purchase order total amount is not showing
             / updating". */
          subTotal: total,
          taxAmount: 0,
          totalAmount: total,
          // Map label → int explicitly. Falling through the StringOrInt
          // JSON converter would land "Confirmed" on 0 (no mapping) and
          // "Cancelled" on 4 — both wrong for our OrderStatus semantics.
          status: PO_STATUS_BY_LABEL[f.status] ?? undefined,
          notes: f.notes || undefined,
          isActive: f.isActive,
          items: wireItems,
        };
      }}
      validate={(f) => {
        if (!f.orderNumber.trim()) return 'PO number is required';
        if (!f.warehouseId) return 'Warehouse is required';
        const goodLines = f.items.filter((l) => l.productId && Number(l.quantity) > 0);
        if (goodLines.length === 0)
          return 'Add at least one line item with a product and quantity > 0.';
        return null;
      }}
    />
  );
}
